export interface LocationLike {
  readonly origin: string;
  readonly pathname: string;
}

function isLoopbackHostname(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase();
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1";
}

function isPrivateIpv4Hostname(hostname: string): boolean {
  const trimmed = hostname.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith("10.") || trimmed.startsWith("192.168.")) {
    return true;
  }
  const match = /^172\.(\d{1,3})\./.exec(trimmed);
  if (!match) {
    return false;
  }
  const octet = Number.parseInt(match[1] ?? "", 10);
  return Number.isFinite(octet) && octet >= 16 && octet <= 31;
}

function isLocalNetworkHostname(hostname: string): boolean {
  return isLoopbackHostname(hostname) || isPrivateIpv4Hostname(hostname);
}

function normalizeExplicitRedirectUriForCurrentSession(
  explicitRedirectUri: string,
  locationLike: LocationLike,
): string {
  try {
    const explicitUrl = new URL(explicitRedirectUri);
    const currentUrl = new URL(locationLike.origin);
    const explicitIsLocalNetwork = isLocalNetworkHostname(explicitUrl.hostname);
    const currentIsLocalNetwork = isLocalNetworkHostname(currentUrl.hostname);
    // If the app is running on a hosted domain, ignore local-network callbacks.
    if (!currentIsLocalNetwork && explicitIsLocalNetwork) {
      const pathname = locationLike.pathname.trim() || "/";
      return pathname === "/" ? `${locationLike.origin}/` : `${locationLike.origin}${pathname}`;
    }
    // In mobile/LAN HTTPS dev mode, avoid sending users to an HTTP localhost callback
    // that the HTTPS Vite server cannot serve.
    if (
      currentUrl.protocol === "https:" &&
      explicitUrl.protocol === "http:" &&
      explicitIsLocalNetwork
    ) {
      explicitUrl.protocol = "https:";
      return explicitUrl.toString();
    }
  } catch {
    return explicitRedirectUri;
  }
  return explicitRedirectUri;
}

export function resolveCognitoRedirectUri(
  explicitRedirectUri: string | undefined,
  locationLike: LocationLike,
): string {
  const explicit = explicitRedirectUri?.trim();
  if (explicit) {
    return normalizeExplicitRedirectUriForCurrentSession(explicit, locationLike);
  }

  const pathname = locationLike.pathname.trim() || "/";
  if (pathname === "/") {
    return `${locationLike.origin}/`;
  }
  return `${locationLike.origin}${pathname}`;
}
